/**
 * Players screen — audience KPIs + user directory.
 *
 * Data sources
 *   - GET /admin/metrics            (users totals + DAU/MAU)
 *   - GET /admin/users              (paginated user list with search/sort)
 *   - POST/DELETE /admin/users/{id}/blacklist (mutations from the row)
 *
 * The list is client-driven: search debounce, sort headers, pager. Row
 * click navigates to /admin/v2/players/:id (the detail screen).
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  errorState,
  escape,
  fetchOrNull,
  fmtInt,
  fmtKobo,
  fmtRelTime,
  fromToQuery,
  mountLineChart,
  mountSortableTable,
  pagerControls,
  skeletonGrid,
} from './common.js';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const PAGE_SIZE_STORAGE = 'adm2-pagesize:players.users';

function _loadUsersPageSize() {
  try {
    const raw = sessionStorage.getItem(PAGE_SIZE_STORAGE);
    const n = raw ? Number(raw) : NaN;
    return PAGE_SIZE_OPTIONS.includes(n) ? n : 25;
  } catch {
    return 25;
  }
}
function _saveUsersPageSize(n) {
  try {
    sessionStorage.setItem(PAGE_SIZE_STORAGE, String(n));
  } catch {
    /* private mode / quota */
  }
}

const state = {
  q: '',
  sort: 'last_seen',
  order: 'desc',
  offset: 0,
  total: 0,
  hasMore: false,
  pageSize: _loadUsersPageSize(),
  // Disciplinary-state filter. Empty string = show everyone; the four
  // named values match the backend `state` param on /admin/users.
  stateFilter: '',
};

export async function renderPlayers(host, ctx) {
  host.innerHTML = skeletonGrid(5);
  try {
    // Growth is optional — if it fails we still render the audience KPIs
    // and the user directory. The panel that consumes it degrades to a
    // short "loading failed" note rather than blocking the whole screen.
    const [metrics, growth] = await Promise.all([
      api('/admin/metrics'),
      fetchOrNull(`/admin/metrics/growth${fromToQuery(ctx.range)}`),
    ]);
    host.innerHTML = _render(metrics, growth);
    _mountCohortTable(host, growth);
    _mountSignupsChart(host, growth);
    _wire(host, ctx);
    await _reloadUsers(host, ctx);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

function _render(metrics, growth) {
  const u = metrics.users || {};
  // Sum of daily signups inside the range picker's window. Falls back
  // to `-` when the growth call errored so we don't render `0` (which
  // would look like a legit zero-signup day). This is the one prominent
  // number that reacts to the range dropdown; total/verified/admins
  // are global counters and DAU/MAU are fixed rolling windows.
  const signupsInWindow = growth
    ? (growth.signups_daily || []).reduce((n, r) => n + Number(r.count || 0), 0)
    : null;
  const audience = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(170px,1fr));">
      ${_bigStat(u.total, 'Total users')}
      ${_bigStat(u.verified, 'Verified')}
      ${_bigStat(u.admins, 'Admins')}
      ${_bigStat(signupsInWindow, 'New · this window')}
    </div>
    <div style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:10px;">
      Total / verified / admins are all-time counters. New · this window
      sums signups across the dates picked in the topbar; change the
      range to update it.
    </div>
  `;
  const active = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));">
      ${_bigStat(u.dau, 'DAU · rolling 24h', 38)}
      ${_bigStat(u.mau, 'MAU · rolling 30d', 38)}
    </div>
    <div style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:10px;">
      Distinct users seen on any authenticated request. Rolling windows
      anchored on now — not affected by the topbar date range.
    </div>
  `;
  return `
    <div class="adm2-section-label">Audience · total base</div>
    ${audience}

    <div class="adm2-section-label">Active users</div>
    ${active}

    ${_renderGrowthPanels(growth)}

    <div class="adm2-panel" style="margin-top:26px;padding:0;overflow:hidden;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;padding:18px 20px;border-bottom:1px solid var(--a-border);flex-wrap:wrap;">
        <div id="adm2-users-summary" style="font:500 13px 'IBM Plex Mono',monospace;color:var(--a-muted);">Loading…</div>
        <div style="position:relative;display:flex;align-items:center;">
          <span style="position:absolute;left:13px;font-size:14px;color:var(--a-faint);pointer-events:none;">⌕</span>
          <input id="adm2-users-search" class="adm2-input" placeholder="Search email or username"
                 value="${escape(state.q)}"
                 style="padding-left:32px;width:260px;max-width:60vw;" />
        </div>
      </div>
      <div id="adm2-users-state-chips" style="display:flex;gap:8px;flex-wrap:wrap;padding:12px 20px;border-bottom:1px solid var(--a-border);">
        ${_stateFilterChips()}
      </div>
      <div id="adm2-users-list" style="overflow-x:auto;padding:8px 4px;"></div>
      <div id="adm2-users-pager"></div>
    </div>
  `;
}

function _stateFilterChips() {
  const chips = [
    { key: '', label: 'All users', color: 'var(--a-muted)', bg: 'transparent' },
    { key: 'penalised', label: 'Any penalty', color: '#F87171', bg: 'rgba(248,113,113,.08)' },
    { key: 'banned', label: 'Banned', color: '#F87171', bg: 'rgba(248,113,113,.12)' },
    { key: 'under_investigation', label: 'Under investigation', color: '#FBBF24', bg: 'rgba(251,191,36,.12)' },
    { key: 'blacklisted', label: 'Blacklisted', color: '#F87171', bg: 'rgba(248,113,113,.12)' },
  ];
  return chips.map((c) => {
    const active = state.stateFilter === c.key;
    const border = active ? c.color : 'var(--a-border)';
    const bg = active ? c.bg : 'transparent';
    const fg = active ? c.color : 'var(--a-muted)';
    return `<button type="button" data-state-filter="${escape(c.key)}"
      style="padding:6px 12px;font:600 11.5px 'IBM Plex Sans',sans-serif;letter-spacing:.03em;border-radius:999px;background:${bg};border:1px solid ${border};color:${fg};cursor:pointer;">
      ${escape(c.label)}
    </button>`;
  }).join('');
}

function _renderGrowthPanels(g) {
  if (!g) {
    // /admin/metrics/growth was optional in the parallel fetch; if it 500'd
    // we still want the users table below to render fine.
    return '';
  }
  const st = g.stickiness || {};
  const ver = g.verification || {};
  const soc = g.social_adoption || {};
  const signups = g.signups_daily || [];
  const cohorts = g.retention_cohorts || [];

  const stickinessCards = `
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(190px,1fr));">
      ${_kpi('DAU / MAU stickiness', st.ratio == null ? '—' : `${(st.ratio * 100).toFixed(1)}%`, `${fmtInt(st.dau)} / ${fmtInt(st.mau)}`)}
      ${_kpi('Verification rate', ver.rate == null ? '—' : `${(ver.rate * 100).toFixed(1)}%`, `${fmtInt(ver.verified)} of ${fmtInt(ver.total)}`)}
      ${_kpi('Social login adoption', soc.rate == null ? '—' : `${(soc.rate * 100).toFixed(1)}%`, `${fmtInt(soc.linked_users)} of ${fmtInt(soc.total_users)}`)}
    </div>
  `;

  const providerRows = (soc.by_provider || [])
    .sort((a, b) => (b.linked_users || 0) - (a.linked_users || 0))
    .map((p) => {
      const pct = soc.total_users ? Math.round((100 * (p.linked_users || 0)) / soc.total_users) : 0;
      return `
        <div style="margin-bottom:11px;">
          <div style="display:flex;justify-content:space-between;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);margin-bottom:5px;">
            <span>${escape(p.provider)}</span>
            <span style="color:var(--a-muted);font-family:'IBM Plex Mono',monospace;">${escape(fmtInt(p.linked_users))} · ${pct}%</span>
          </div>
          <div class="adm2-bar-track" style="height:8px;"><div class="adm2-bar-fill" style="width:${pct}%;background:#7C53E8;"></div></div>
        </div>
      `;
    })
    .join('');

  const signupChart = `<div id="adm2-signups-chart"></div>`;

  return `
    <div class="adm2-section-label">Stickiness &amp; adoption</div>
    ${stickinessCards}

    <div class="adm2-two" style="margin-top:16px;">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Signups — daily</div>
        <div class="adm2-panel-sub">windowed to the date range · dense series (zero-fill on quiet days)</div>
        <div style="margin-top:16px;">${signupChart}</div>
      </div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">Social login by provider</div>
        <div class="adm2-panel-sub">linked users · % of total</div>
        <div style="margin-top:16px;">${providerRows || '<div class="adm2-kpi-hint">No social identities yet.</div>'}</div>
      </div>
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div class="adm2-panel-title">Weekly retention cohorts</div>
      <div class="adm2-panel-sub">click a header to sort · D1 / D7 / D30 return rate · immature cells show as dashes</div>
      <div id="growth-cohorts" style="margin-top:14px;"></div>
      <div style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-faint);margin-top:12px;">
        ${escape(g.last_seen_caveat || '')}
      </div>
    </div>
  `;
}

function _mountCohortTable(host, g) {
  const target = host.querySelector('#growth-cohorts');
  if (!target) return;
  const cohorts = g?.retention_cohorts || [];
  if (!cohorts.length) {
    target.innerHTML = '<div class="adm2-kpi-hint">No cohorts to show yet.</div>';
    return;
  }
  const cell = (bucket) => {
    if (!bucket || bucket.rate == null) {
      return '<span style="color:var(--a-faint);">—</span>';
    }
    const pct = Math.round(bucket.rate * 100);
    const bg = pct >= 40 ? 'var(--a-ok-bg)' : pct >= 20 ? 'var(--a-warn-bg)' : 'var(--a-bad-bg)';
    const fg = pct >= 40 ? 'var(--a-ok)' : pct >= 20 ? 'var(--a-warn)' : 'var(--a-bad)';
    return `
      <span style="display:inline-block;padding:3px 9px;border-radius:6px;background:${bg};color:${fg};font-weight:600;">${pct}%</span>
      <span style="color:var(--a-faint);font-size:11px;margin-left:4px;">${escape(fmtInt(bucket.active))}</span>
    `;
  };
  mountSortableTable(target, {
    columns: [
      {
        key: 'cohort_start',
        label: 'Cohort start',
        text: true,
        get: (r) => r.cohort_start || '',
        cell: (r) => escape(r.cohort_start || '—'),
        className: 'mono',
      },
      {
        key: 'size',
        label: 'Size',
        right: true,
        get: (r) => Number(r.size || 0),
        cell: (r) => escape(fmtInt(r.size)),
        className: 'mono',
      },
      {
        key: 'd1',
        label: 'D1',
        right: true,
        // Sort by rate ascending puts the worst cohorts first; use raw
        // rate (null → nulls-last handled by _sortRows).
        get: (r) => (r.d1 ? r.d1.rate : null),
        cell: (r) => cell(r.d1),
      },
      {
        key: 'd7',
        label: 'D7',
        right: true,
        get: (r) => (r.d7 ? r.d7.rate : null),
        cell: (r) => cell(r.d7),
      },
      {
        key: 'd30',
        label: 'D30',
        right: true,
        get: (r) => (r.d30 ? r.d30.rate : null),
        cell: (r) => cell(r.d30),
      },
    ],
    rows: cohorts,
    defaultSort: { key: 'cohort_start', dir: 'desc' },
    minWidth: 560,
    emptyMessage: 'No cohorts to show yet.',
    pageSizeKey: 'players.cohorts',
  });
}

function _kpi(label, value, sub) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:17px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 24px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
      <div style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:7px;">${escape(sub)}</div>
    </div>
  `;
}

function _mountSignupsChart(host, g) {
  const target = host.querySelector('#adm2-signups-chart');
  if (!target) return;
  const signups = g?.signups_daily || [];
  mountLineChart(target, {
    series: [
      {
        name: 'Signups',
        color: '#8B6BF2',
        values: signups.map((r) => Number(r.count || 0)),
      },
    ],
    xLabels: signups.map((r) => r.date),
    height: 200,
    emptyMessage: 'No signups in this window.',
  });
}

function _bigStat(value, label, size = 32) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:20px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 ${size}px/1 'IBM Plex Mono',monospace;color:var(--a-ink);">${escape(fmtInt(value))}</div>
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);margin-top:10px;">${escape(label)}</div>
    </div>
  `;
}

function _wire(host, ctx) {
  const search = host.querySelector('#adm2-users-search');
  if (search) {
    let t;
    search.addEventListener('input', (e) => {
      clearTimeout(t);
      t = setTimeout(() => {
        state.q = e.target.value.trim();
        state.offset = 0;
        void _reloadUsers(host, ctx);
      }, 250);
    });
  }
  _wireStateFilter(host, ctx);
}

function _wireStateFilter(host, ctx) {
  host.querySelectorAll('[data-state-filter]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.stateFilter;
      if (state.stateFilter === key) return;
      state.stateFilter = key;
      state.offset = 0;
      // Re-render the chip row so the active state moves; the summary +
      // table below reload via _reloadUsers.
      const bar = host.querySelector('#adm2-users-state-chips');
      if (bar) {
        bar.innerHTML = _stateFilterChips();
        _wireStateFilter(host, ctx);
      }
      void _reloadUsers(host, ctx);
    });
  });
}

async function _reloadUsers(host, ctx) {
  const list = host.querySelector('#adm2-users-list');
  const summary = host.querySelector('#adm2-users-summary');
  const pager = host.querySelector('#adm2-users-pager');
  if (!list || !summary || !pager) return;
  list.innerHTML = `<div class="adm2-skl" style="height:180px;width:100%;border-radius:10px;margin:12px;"></div>`;
  try {
    const params = new URLSearchParams({
      limit: String(state.pageSize),
      offset: String(state.offset),
      sort: state.sort,
      order: state.order,
    });
    if (state.q) params.set('q', state.q);
    if (state.stateFilter) params.set('state', state.stateFilter);
    const page = await api(`/admin/users?${params.toString()}`);
    state.total = page.total;
    state.hasMore = page.has_more;
    summary.textContent = state.q
      ? `${page.total.toLocaleString('en-NG')} match "${state.q}" · showing ${page.items.length}`
      : `${page.total.toLocaleString('en-NG')} users · showing ${page.offset + 1}–${page.offset + page.items.length}`;
    list.innerHTML = _renderTable(page.items);
    pager.innerHTML = _renderPager(page.items.length);
    _wireRows(host, ctx);
    _wireHeaders(host, ctx);
    _wirePager(host, ctx);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    list.innerHTML = errorState(err);
  }
}

function _renderTable(items) {
  if (!items.length) {
    return `<div style="padding:48px 24px;text-align:center;color:var(--a-faint);">No users match your search.</div>`;
  }
  const th = (key, label, right = false) => {
    const active = state.sort === key;
    const arrow = active ? (state.order === 'asc' ? '↑' : '↓') : '';
    return `<th class="${right ? 'right' : ''}" data-sort="${escape(key)}" style="cursor:pointer;color:${active ? 'var(--a-ink)' : 'var(--a-muted)'};padding-top:16px;">${escape(label)} ${escape(arrow)}</th>`;
  };
  const rows = items
    .map((u) => {
      const badges = [];
      if (u.is_admin) badges.push('<span class="adm2-chip" style="color:var(--a-brand);background:var(--a-brand-soft);border-color:transparent;">ADMIN</span>');
      if (u.account_state === 'banned') {
        badges.push('<span class="adm2-chip" style="color:#fff;background:rgba(248,113,113,.32);border-color:rgba(248,113,113,.55);">BANNED</span>');
      } else if (u.account_state === 'under_investigation') {
        badges.push('<span class="adm2-chip" style="color:#fff;background:rgba(251,191,36,.28);border-color:rgba(251,191,36,.55);">UNDER INVESTIGATION</span>');
      }
      if (u.is_blacklisted) badges.push('<span class="adm2-chip r">BLACKLISTED</span>');
      if (u.bonus_powerup_only) badges.push('<span class="adm2-chip" style="color:#B7A5F5;background:rgba(139,107,242,.16);border-color:rgba(139,107,242,.4);">POWERUP-ONLY BONUS</span>');
      if (!u.email_verified) badges.push('<span class="adm2-chip w">UNVERIFIED</span>');
      const status = u.status && u.status !== 'active' ? u.status : (u.email_verified ? 'verified' : 'pending');
      const actLabel = u.is_admin ? 'Blacklist' : u.is_blacklisted ? 'Unblacklist' : 'Blacklist';
      const disabled = u.is_admin;
      return `
        <tr>
          <td>
            <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
              <a class="link" data-open-user="${escape(u.id)}">${escape(u.email)}</a>
              ${badges.join('')}
            </div>
            <div style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:4px;">${escape(u.username ? '@' + u.username : '—')}</div>
          </td>
          <td>${escape(status)}</td>
          <td>${escape(fmtRelTime(u.last_seen_at))}</td>
          <td>${escape(fmtRelTime(u.created_at))}</td>
          <td class="right mono">${escape(fmtKobo(u.wallet_kobo))}</td>
          <td class="right mono" style="color:var(--a-warm-ink);">${escape(fmtKobo(u.bonus_kobo))}</td>
          <td class="right mono">${escape(fmtInt(u.fifty_balance))}</td>
          <td class="right mono">${escape(fmtInt(u.eraser_balance))}</td>
          <td class="right mono">${escape(fmtKobo(u.deposits_kobo))}</td>
          <td class="right mono" style="color:var(--a-ok);">${escape(fmtKobo(u.earnings_kobo))}</td>
          <td class="right">
            <button type="button" ${disabled ? 'disabled' : ''}
              data-blacklist-toggle="${u.is_blacklisted ? 'remove' : 'add'}"
              data-user-id="${escape(u.id)}"
              data-user-email="${escape(u.email)}"
              class="adm2-btn"
              style="${disabled ? 'opacity:.4;cursor:not-allowed;' : ''}">
              ${escape(actLabel)}
            </button>
          </td>
        </tr>
      `;
    })
    .join('');
  return `
    <table class="adm2-table" style="min-width:1420px;">
      <thead>
        <tr>
          ${th('email', 'User')}
          <th>Status</th>
          ${th('last_seen', 'Last seen')}
          ${th('created', 'Joined')}
          ${th('wallet', 'Wallet', true)}
          ${th('bonus', 'Bonus', true)}
          ${th('fifty', '50/50', true)}
          ${th('eraser', 'Erasers', true)}
          ${th('deposits', 'Deposits', true)}
          ${th('earnings', 'Earnings', true)}
          <th class="right">Actions</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

function _renderPager(itemCount) {
  const shownFrom = itemCount ? state.offset + 1 : 0;
  const shownTo = state.offset + itemCount;
  const totalPages = Math.max(1, Math.ceil(state.total / state.pageSize));
  const page = Math.floor(state.offset / state.pageSize) + 1;
  return pagerControls({
    pageSize: state.pageSize,
    page,
    totalPages,
    total: state.total,
    shownFrom,
    shownTo,
    hasPrev: state.offset > 0,
    hasNext: state.hasMore,
    sizeSelectId: 'adm2-users-size',
    prevId: 'adm2-users-prev',
    nextId: 'adm2-users-next',
  });
}

function _wireRows(host, ctx) {
  host.querySelectorAll('[data-open-user]').forEach((el) => {
    el.addEventListener('click', () => {
      ctx.navigate(`/admin/v2/players/${el.dataset.openUser}`);
    });
  });
  host.querySelectorAll('[data-blacklist-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => _onToggleBlacklist(btn, host, ctx));
  });
}
function _wireHeaders(host, ctx) {
  host.querySelectorAll('[data-sort]').forEach((th) => {
    th.addEventListener('click', () => {
      const key = th.dataset.sort;
      if (state.sort === key) {
        state.order = state.order === 'asc' ? 'desc' : 'asc';
      } else {
        state.sort = key;
        state.order = key === 'email' ? 'asc' : 'desc';
      }
      state.offset = 0;
      void _reloadUsers(host, ctx);
    });
  });
}
function _wirePager(host, ctx) {
  const prev = host.querySelector('#adm2-users-prev');
  const next = host.querySelector('#adm2-users-next');
  const size = host.querySelector('#adm2-users-size');
  if (prev)
    prev.addEventListener('click', () => {
      state.offset = Math.max(0, state.offset - state.pageSize);
      void _reloadUsers(host, ctx);
    });
  if (next)
    next.addEventListener('click', () => {
      state.offset += state.pageSize;
      void _reloadUsers(host, ctx);
    });
  if (size)
    size.addEventListener('change', (ev) => {
      const n = Number(ev.target.value);
      if (!PAGE_SIZE_OPTIONS.includes(n)) return;
      state.pageSize = n;
      state.offset = 0;
      _saveUsersPageSize(n);
      void _reloadUsers(host, ctx);
    });
}

async function _onToggleBlacklist(btn, host, ctx) {
  const action = btn.dataset.blacklistToggle; // 'add' | 'remove'
  const userId = btn.dataset.userId;
  const email = btn.dataset.userEmail;
  const verb = action === 'add' ? 'Blacklist' : 'Unblacklist';
  if (!window.confirm(`${verb} ${email}?`)) return;
  btn.disabled = true;
  try {
    await api(`/admin/users/${encodeURIComponent(userId)}/blacklist`, {
      method: action === 'add' ? 'POST' : 'DELETE',
    });
    toast.success(`${verb}ed ${email}.`);
    await _reloadUsers(host, ctx);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    btn.disabled = false;
  }
}
